import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRevealGroup } from 'hooks/useReveal';
import { STATS, TOURNAMENTS, PARTNERS, FOUNDERS } from './history.data';

/**
 * Чотири блоки статичного вмісту сторінки історії — ADR-0011.
 *
 * Кожен зі своїм `useRevealGroup`, а не одним спільним на всю сторінку:
 * дані статичні (не чекають на бекенд), тож ефект застосовується вже при
 * монтуванні, а власний рахівник каскаду не залежить від того, скільки
 * елементів було вище в тексті (інакше картка №11 у партнерах чекала б
 * `11 × крок` іще й від абзаців і фото, що йшли перед нею).
 */

export function HistoryFacts({ language }) {
  const groupRef = useRevealGroup('.history-fact', { step: 70, threshold: 0.2 });

  return (
    <div className='history-facts' ref={groupRef}>
      {STATS.map((stat) => (
        <div className='history-fact' key={stat.value + stat.ua}>
          <span className='history-fact-value tnum'>{stat.value}</span>
          <span className='history-fact-label'>{language === 'en' ? stat.en : stat.ua}</span>
          <span className='history-fact-hint'>{language === 'en' ? stat.hintEn : stat.hintUa}</span>
        </div>
      ))}
    </div>
  );
}

export function HistoryTimeline({ language }) {
  const groupRef = useRevealGroup('.history-timeline-item', { step: 90, threshold: 0.2 });

  return (
    <div className='history-block'>
      <span className='section-label'>{language === 'en' ? 'Signature events' : 'Знакові турніри'}</span>
      <h3 className='history-block-title'>
        {language === 'en' ? 'Tournaments the community remembers' : 'Турніри, які запам’ятала спільнота'}
      </h3>

      <ol className='history-timeline' ref={groupRef}>
        {TOURNAMENTS.map((item) => (
          <li className='history-timeline-item' key={item.key}>
            <span className='history-timeline-years tnum'>{item.years}</span>
            <span className='history-timeline-dot' aria-hidden='true' />
            <span className='history-timeline-name'>{language === 'en' ? item.nameEn : item.nameUa}</span>
            <span className='history-timeline-note'>{language === 'en' ? item.noteEn : item.noteUa}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function HistoryPartners({ language }) {
  const groupRef = useRevealGroup('.history-partner', { step: 35, threshold: 0.1 });

  return (
    <div className='history-block'>
      <span className='section-label'>{language === 'en' ? 'Two decades of alliances' : 'Двадцять років домовленостей'}</span>
      <h3 className='history-block-title'>
        {language === 'en' ? 'Agreements that shaped the movement' : 'Угоди, що формували рух'}
      </h3>

      <ul className='history-partners' ref={groupRef}>
        {PARTNERS.map((partner) => (
          <li className='history-partner' key={partner.key}>
            <span className='history-partner-year tnum'>{partner.year}</span>
            <span className='history-partner-short'>{language === 'en' ? partner.shortEn : partner.short}</span>
            {(language === 'en' ? partner.nameEn : partner.nameUa) && (
              <span className='history-partner-name'>{language === 'en' ? partner.nameEn : partner.nameUa}</span>
            )}
            <span className='history-partner-person'>{language === 'en' ? partner.personEn : partner.personUa}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function HistoryFounders({ language }) {
  const groupRef = useRevealGroup('.history-founder-item', { step: 80, threshold: 0.2 });

  return (
    <div className='history-block'>
      <span className='section-label'>{language === 'en' ? 'On the international stage' : 'На міжнародній арені'}</span>
      <h3 className='history-block-title'>
        {language === 'en' ? 'Founding member and federation partner' : 'Серед засновників і федерацій-партнерів'}
      </h3>

      <ul className='history-founders' ref={groupRef}>
        {FOUNDERS.map((founder) => (
          <li className='history-founder-item' key={founder.key}>
            <span className='history-founder-icon'>
              <FontAwesomeIcon icon={founder.icon} />
            </span>
            <span className='history-founder-text'>{language === 'en' ? founder.textEn : founder.textUa}</span>
            <span className='history-founder-year tnum'>{founder.year}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
